/**
 * Full-screen viewer for PDP gallery images.
 *
 * Each image slide is covered by an .image-zoom-open button (see
 * snippets/product-media.liquid), so tapping the image slides the viewer
 * up over the page. A thumbnail strip at the bottom jumps between images;
 * arrows and arrow keys also work on desktop and swipe works on mobile.
 *
 * Zoom works like Myntra's mobile viewer: a single tap or click zooms into
 * that point and the next one zooms back out, pinch zooms up to MAX_SCALE,
 * the mouse wheel zooms at the cursor on desktop, and dragging pans. At 1x,
 * dragging down pulls the viewer with the finger and closes it past
 * DISMISS_DISTANCE (or on a quick flick), otherwise it snaps back. Swipe navigation only runs at 1x so panning never changes
 * slides.
 *
 * On desktop (DESKTOP_QUERY) the viewer instead stacks every image at full
 * width and natural height in a vertically scrolling list, opened centred
 * on the clicked image.
 *
 * Listeners are delegated at document level so they survive the
 * [data-variant-featured-media] outerHTML swaps performed by variants.js
 * on variant/colour changes.
 */

(function () {
  if (window.__one8ImageZoom) return;
  window.__one8ImageZoom = true;

  var SWIPE_DISTANCE = 50;
  var TAP_DISTANCE = 10;
  var TAP_SCALE = 2.5;
  var MAX_SCALE = 4;
  var DISMISS_DISTANCE = 120;
  var DISMISS_VELOCITY = 0.6;
  var CLOSE_DURATION = 360;
  var WHEEL_ZOOM_SPEED = 0.0025;
  var DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');

  var modal = null;
  var modalImg = null;
  var modalScroller = null;
  var modalThumbs = null;
  var modalStack = null;
  var isDesktop = false;

  var items = [];
  var currentIndex = 0;

  var scale = 1;
  var translateX = 0;
  var translateY = 0;

  var pointers = new Map();
  var gesture = null;
  var frameRequested = false;
  var closeTimer = null;
  var wheelTimer = null;

  function getModal() {
    if (modal) return modal;

    modal = document.createElement('div');
    modal.className = 'image-zoom-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.hidden = true;
    modal.innerHTML =
      '<button type="button" class="image-zoom-modal__close" aria-label="Close">' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">' +
      '<line x1="5" y1="5" x2="19" y2="19"></line>' +
      '<line x1="19" y1="5" x2="5" y2="19"></line>' +
      '</svg></button>' +
      '<button type="button" class="image-zoom-modal__nav image-zoom-modal__nav--prev" aria-label="Previous image">' +
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<polyline points="15 18 9 12 15 6"></polyline>' +
      '</svg></button>' +
      '<button type="button" class="image-zoom-modal__nav image-zoom-modal__nav--next" aria-label="Next image">' +
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<polyline points="9 18 15 12 9 6"></polyline>' +
      '</svg></button>' +
      '<div class="image-zoom-modal__scroller">' +
      '<img class="image-zoom-modal__img" alt="" draggable="false">' +
      '</div>' +
      '<div class="image-zoom-modal__thumbs"></div>' +
      '<div class="image-zoom-modal__stack"></div>';
    document.body.appendChild(modal);

    modalImg = modal.querySelector('.image-zoom-modal__img');
    modalScroller = modal.querySelector('.image-zoom-modal__scroller');
    modalThumbs = modal.querySelector('.image-zoom-modal__thumbs');
    modalStack = modal.querySelector('.image-zoom-modal__stack');

    modal
      .querySelector('.image-zoom-modal__close')
      .addEventListener('click', closeModal);
    modal
      .querySelector('.image-zoom-modal__nav--prev')
      .addEventListener('click', function () {
        goTo(currentIndex - 1);
      });
    modal
      .querySelector('.image-zoom-modal__nav--next')
      .addEventListener('click', function () {
        goTo(currentIndex + 1);
      });

    modalThumbs.addEventListener('click', function (event) {
      var thumb = event.target.closest('.image-zoom-modal__thumb');
      if (thumb) goTo(Number(thumb.dataset.index));
    });

    modalScroller.addEventListener('pointerdown', onPointerDown);
    modalScroller.addEventListener('pointermove', onPointerMove);
    modalScroller.addEventListener('pointerup', onPointerUp);
    modalScroller.addEventListener('pointercancel', onPointerCancel);

    modalScroller.addEventListener(
      'wheel',
      function (event) {
        event.preventDefault();

        var delta = event.deltaY;
        if (event.deltaMode === 1) delta *= 16;
        if (event.deltaMode === 2) delta *= window.innerHeight;

        var speed = event.ctrlKey ? 0.01 : WHEEL_ZOOM_SPEED;
        var nextScale = scale * Math.exp(-delta * speed);

        modal.classList.add('is-gesturing');
        clearTimeout(wheelTimer);
        wheelTimer = setTimeout(function () {
          modal.classList.remove('is-gesturing');
        }, 150);

        zoomAt(nextScale, event.clientX, event.clientY);
      },
      { passive: false }
    );

    document.addEventListener('keydown', function (event) {
      if (modal.hidden || !modal.classList.contains('is-open')) return;
      if (event.key === 'Escape') closeModal();
      if (isDesktop) return;
      if (event.key === 'ArrowLeft') goTo(currentIndex - 1);
      if (event.key === 'ArrowRight') goTo(currentIndex + 1);
    });

    window.addEventListener('resize', function () {
      if (!modal.hidden) applyTransform();
    });

    DESKTOP_QUERY.addEventListener('change', closeModal);

    return modal;
  }

  function applyTransform() {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(renderTransform);
  }

  function renderTransform() {
    frameRequested = false;
    clampTranslate();
    modalImg.style.transform =
      'translate(' + translateX + 'px, ' + translateY + 'px) scale(' + scale + ')';
    modal.classList.toggle('is-zoomed', scale > 1);
  }

  function clampTranslate() {
    var width = modalImg.offsetWidth * scale;
    var height = modalImg.offsetHeight * scale;
    var maxX = Math.max(0, (width - modalScroller.clientWidth) / 2);
    var maxY = Math.max(0, (height - modalScroller.clientHeight) / 2);

    translateX = Math.min(maxX, Math.max(-maxX, translateX));
    translateY = Math.min(maxY, Math.max(-maxY, translateY));
  }

  function zoomAt(nextScale, clientX, clientY) {
    nextScale = Math.min(MAX_SCALE, Math.max(1, nextScale));

    var rect = modalScroller.getBoundingClientRect();
    var offsetX = clientX - (rect.left + rect.width / 2);
    var offsetY = clientY - (rect.top + rect.height / 2);
    var ratio = nextScale / scale;

    translateX = offsetX - (offsetX - translateX) * ratio;
    translateY = offsetY - (offsetY - translateY) * ratio;
    scale = nextScale;

    if (scale === 1) {
      translateX = 0;
      translateY = 0;
    }

    applyTransform();
  }

  function resetZoom() {
    scale = 1;
    translateX = 0;
    translateY = 0;
    if (modalImg) applyTransform();
  }

  function setDismissOffset(offset) {
    var progress = Math.min(1, offset / window.innerHeight);
    modal.style.setProperty('--image-zoom-drag', offset + 'px');
    modalImg.style.transform = 'scale(' + (1 - progress * 0.3) + ')';
  }

  function clearDismissOffset() {
    modal.classList.remove('is-dragging');
    modal.style.removeProperty('--image-zoom-drag');
  }

  function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  function midpoint(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  function startGesture() {
    var points = Array.from(pointers.values());

    if (points.length >= 2) {
      if (gesture && gesture.dismissing) {
        clearDismissOffset();
        applyTransform();
      }
      gesture = {
        type: 'pinch',
        startDistance: distance(points[0], points[1]) || 1,
        startScale: scale,
        lastMid: midpoint(points[0], points[1]),
      };
    } else if (points.length === 1) {
      gesture = {
        type: 'drag',
        startX: points[0].x,
        startY: points[0].y,
        lastX: points[0].x,
        lastY: points[0].y,
        startTime: Date.now(),
        moved: false,
        axis: null,
        dismissing: false,
        wasPinch: gesture && gesture.type === 'pinch',
      };
    } else {
      gesture = null;
    }
  }

  function onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    modalScroller.setPointerCapture(event.pointerId);
    pointers.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
      target: event.target,
    });
    modal.classList.add('is-gesturing');
    startGesture();
  }

  function onPointerMove(event) {
    var pointer = pointers.get(event.pointerId);
    if (!pointer || !gesture) return;

    pointer.x = event.clientX;
    pointer.y = event.clientY;

    if (gesture.type === 'pinch') {
      var points = Array.from(pointers.values());
      if (points.length < 2) return;

      var mid = midpoint(points[0], points[1]);
      var nextScale =
        gesture.startScale * (distance(points[0], points[1]) / gesture.startDistance);

      translateX += mid.x - gesture.lastMid.x;
      translateY += mid.y - gesture.lastMid.y;
      gesture.lastMid = mid;
      zoomAt(nextScale, mid.x, mid.y);
      return;
    }

    var totalX = event.clientX - gesture.startX;
    var totalY = event.clientY - gesture.startY;
    if (Math.abs(totalX) > TAP_DISTANCE || Math.abs(totalY) > TAP_DISTANCE) {
      gesture.moved = true;
      if (!gesture.axis) {
        gesture.axis = Math.abs(totalY) > Math.abs(totalX) ? 'y' : 'x';
      }
    }

    if (scale > 1) {
      translateX += event.clientX - gesture.lastX;
      translateY += event.clientY - gesture.lastY;
      applyTransform();
    } else if (gesture.axis === 'y' && !gesture.wasPinch) {
      gesture.dismissing = true;
      modal.classList.add('is-dragging');
      setDismissOffset(Math.max(0, totalY));
    }

    gesture.lastX = event.clientX;
    gesture.lastY = event.clientY;
  }

  function onPointerUp(event) {
    var pointer = pointers.get(event.pointerId);
    pointers.delete(event.pointerId);
    if (!pointer) return;

    var finished = gesture;

    if (pointers.size > 0) {
      startGesture();
      return;
    }

    gesture = null;
    modal.classList.remove('is-gesturing');

    if (!finished || finished.type !== 'drag' || finished.wasPinch) return;

    var dx = event.clientX - finished.startX;
    var dy = event.clientY - finished.startY;

    if (finished.dismissing) {
      var velocity = dy / Math.max(1, Date.now() - finished.startTime);
      clearDismissOffset();

      if (dy > DISMISS_DISTANCE || (dy > TAP_DISTANCE * 3 && velocity > DISMISS_VELOCITY)) {
        modal.style.setProperty('--image-zoom-drag', Math.max(0, dy) + 'px');
        void modal.offsetHeight;
        closeModal();
      } else {
        applyTransform();
      }
      return;
    }

    if (!finished.moved) {
      handleTap(pointer, event);
      return;
    }

    if (
      scale === 1 &&
      finished.axis === 'x' &&
      Math.abs(dx) > SWIPE_DISTANCE
    ) {
      goTo(dx < 0 ? currentIndex + 1 : currentIndex - 1);
    }
  }

  function onPointerCancel(event) {
    pointers.delete(event.pointerId);
    if (pointers.size > 0) {
      startGesture();
      return;
    }
    if (gesture && gesture.dismissing) {
      clearDismissOffset();
      applyTransform();
    }
    gesture = null;
    modal.classList.remove('is-gesturing');
  }

  function handleTap(pointer, event) {
    if (scale > 1) {
      resetZoom();
    } else if (pointer.target === modalImg) {
      zoomAt(TAP_SCALE, event.clientX, event.clientY);
    } else {
      closeModal();
    }
  }

  function collectButtons() {
    return document.querySelectorAll(
      '[data-variant-featured-media] .image-zoom-open'
    );
  }

  function collectItems() {
    return Array.prototype.map.call(collectButtons(), function (button) {
      return {
        src: button.dataset.zoomSrc,
        thumb: button.dataset.zoomThumb || button.dataset.zoomSrc,
        alt: button.dataset.zoomAlt || '',
        ratio: Number(button.dataset.zoomRatio) || 0,
      };
    });
  }

  function renderThumbs() {
    modalThumbs.innerHTML = '';
    modalThumbs.hidden = items.length < 2;

    items.forEach(function (item, index) {
      var thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = 'image-zoom-modal__thumb';
      thumb.dataset.index = String(index);
      thumb.setAttribute('aria-label', 'Image ' + (index + 1) + ' of ' + items.length);

      var img = document.createElement('img');
      img.src = item.thumb;
      img.alt = '';
      img.loading = 'lazy';
      img.draggable = false;

      thumb.appendChild(img);
      modalThumbs.appendChild(thumb);
    });
  }

  function renderStack() {
    modalStack.innerHTML = '';

    items.forEach(function (item, index) {
      var img = document.createElement('img');
      img.className = 'image-zoom-modal__stack-img';
      img.src = item.src;
      img.alt = item.alt;
      img.draggable = false;
      if (index !== currentIndex) img.loading = 'lazy';
      if (item.ratio) img.style.aspectRatio = String(item.ratio);
      modalStack.appendChild(img);
    });
  }

  function updateThumbs() {
    var active = null;

    modalThumbs.querySelectorAll('.image-zoom-modal__thumb').forEach(function (thumb) {
      var isActive = Number(thumb.dataset.index) === currentIndex;
      thumb.classList.toggle('is-active', isActive);
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
        active = thumb;
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    if (active) {
      modalThumbs.scrollTo({
        left: active.offsetLeft - (modalThumbs.clientWidth - active.offsetWidth) / 2,
        behavior: 'smooth',
      });
    }
  }

  function goTo(index) {
    if (!items.length) return;

    currentIndex = (index + items.length) % items.length;
    var item = items[currentIndex];

    if (isDesktop) {
      var target = modalStack.children[currentIndex];
      if (target) {
        modalStack.scrollTop =
          target.offsetTop +
          Math.max(0, (target.offsetHeight - modalStack.clientHeight) / 2);
      }
      return;
    }

    resetZoom();
    modalImg.src = item.src;
    modalImg.alt = item.alt;

    var navHidden = items.length < 2;
    modal
      .querySelectorAll('.image-zoom-modal__nav')
      .forEach(function (button) {
        button.hidden = navHidden;
      });

    updateThumbs();
    preload(currentIndex + 1);
    preload(currentIndex - 1);
  }

  function preload(index) {
    if (items.length < 2) return;
    var item = items[(index + items.length) % items.length];
    if (item) new Image().src = item.src;
  }

  function openModal(clickedButton) {
    var el = getModal();

    clearTimeout(closeTimer);
    clearDismissOffset();

    items = collectItems();
    var index = Array.prototype.indexOf.call(collectButtons(), clickedButton);

    isDesktop = DESKTOP_QUERY.matches;
    currentIndex = index < 0 ? 0 : index;
    el.classList.toggle('is-desktop', isDesktop);
    modalThumbs.innerHTML = '';
    modalStack.innerHTML = '';
    if (isDesktop) {
      renderStack();
    } else {
      renderThumbs();
    }

    el.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    goTo(currentIndex);

    void el.offsetHeight;
    el.classList.add('is-open');
    el.querySelector('.image-zoom-modal__close').focus({ preventScroll: true });
  }

  function closeModal() {
    if (!modal || modal.hidden || !modal.classList.contains('is-open')) return;

    pointers.clear();
    gesture = null;
    modal.classList.remove('is-gesturing', 'is-dragging', 'is-open');

    clearTimeout(closeTimer);
    closeTimer = setTimeout(function () {
      modal.hidden = true;
      modal.style.removeProperty('--image-zoom-drag');
      resetZoom();
      modalImg.src = '';
      modalStack.innerHTML = '';
      document.documentElement.style.overflow = '';
    }, CLOSE_DURATION);
  }

  document.addEventListener('click', function (event) {
    var button =
      event.target instanceof Element
        ? event.target.closest('.image-zoom-open')
        : null;
    if (!button) return;

    event.preventDefault();
    openModal(button);
  });
})();
